/* eslint-disable */

import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";
import { isNative, clearToken, NATIVE_AUTH_URL_PREFIX } from "./nativeSession";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      return r.data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: skip /auth/me if returning from OAuth callback — AuthCallback handles it
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  // Native iOS: Google OAuth returns via com.workmetier.app://auth#session_id=…
  useEffect(() => {
    if (!isNative()) return undefined;
    let handle;
    (async () => {
      const { App } = await import("@capacitor/app");
      handle = await App.addListener("appUrlOpen", async ({ url }) => {
        if (!url || !url.startsWith(NATIVE_AUTH_URL_PREFIX)) return;
        const m = url.match(/session_id=([^&]+)/);
        try { const { Browser } = await import("@capacitor/browser"); await Browser.close(); } catch { /* already closed */ }
        if (!m) return;
        try {
          const r = await api.post("/auth/google", { session_id: decodeURIComponent(m[1]) });
          setUser(r.data);
          window.location.assign("/workspaces");
        } catch {
          window.location.assign("/login");
        }
      });
    })();
    return () => { if (handle) handle.remove(); };
  }, []);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setUser(r.data);
    return r.data;
  };

  const register = async (data) => {
    const r = await api.post("/auth/register", data);
    setUser(r.data);
    return r.data;
  };

  const googleLogin = async () => {
    if (isNative()) {
      // The native shell's origin is app-local; Google must finish on the real web origin,
      // which bounces back into the app via the com.workmetier.app:// URL scheme.
      const back = `${process.env.REACT_APP_BACKEND_URL}/auth/native-return`;
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url: `https://auth.emergentagent.com/?redirect=${encodeURIComponent(back)}` });
      return;
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + "/workspaces";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    clearToken();
    setUser(null);
  };

  // Session 60.24 · App Store §5.1.1(v) — in-app account deletion.
  // Backend anonymizes the user, invalidates every session, wipes personal
  // preferences, and clears the session cookie. We only need to drop the
  // local user state on success.
  const deleteAccount = async () => {
    await api.delete("/auth/me");
    clearToken();
    setUser(null);
  };

  const updateProfile = async (data) => {
    const r = await api.put("/auth/me", data);
    setUser(r.data);
    return r.data;
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, googleLogin, logout, deleteAccount, refresh, updateProfile, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
